import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import cron from 'cron-parser';
import { SquareArrowOutUpRight } from 'lucide-react';
import Moment from 'moment-timezone';
import { Col, FormGroup, Label, Row, Badge, DefaultTooltip, SimpleAlert } from 'twyne-new.ui'

import AlertForm from 'components/form/Alert';
import Input from 'components/form/Input';
import Select from 'components/form/Select';
import Switch from 'components/form/Switch';
import appStore from 'stores/app';
import { defaultDateTimeFormat } from 'utils';

import { usePostStore } from '../store';

const propTypes = {
  disabled: PropTypes.bool,
  data: PropTypes.object,
  sweepCycles: PropTypes.array,
  hasConfirmation: PropTypes.bool,
  postOpts: PropTypes.object,
  onChange: PropTypes.func,
};

export default class SweepOptions extends React.Component {
  state = {}
  scaIntegration = null

  validationErrors = {
    isFlightDatesNotCoveringLastContests: 'There are contests between the flight-dates selected, please select another range.',
    hasFlightStartDate: 'The flight-start-date is required.',
    hasEndDateWhenIsStatic: 'When the Winner Frequency is Just Once the flight-end-date is required.',
    isFlightEndGreatherThanStart: 'The flight-end date must be greater than flight-start date',
    hasNextCycles: 'The selected flight-dates will not result in any new contests being created.',
    isFlightDatesCoveringAtLeastOneRepeatableContest: 'The Winner Frequency you have selected would result in the contest end date being after the Sweeps flight end date.',
  }

  warningTipMessages = {
    partialConstestAtLast: 'This contest will have less days of the frequency than you have selected. Be sure you want to do this or select another flight date.',
  }

  handleChangeWinnerFrequency = selectedWinnerFrequency => {
    this.setState({ selectedWinnerFrequency })
    this.props.onChange({ winnerFrequency: selectedWinnerFrequency.value })
  }

  ContestPreviewItem = ({ dates: [cstart, cend, idx], now, color }) => {
    const { endsAt } = this.props.postOpts
    let content = null
    if (cstart && cend) {
      const Content = ({ end }) => <span>Contest #{idx} from <b>{cstart.format(defaultDateTimeFormat)}</b> to <b>{(end || cend).format(defaultDateTimeFormat)}</b></span>

      if (endsAt && cend.isAfter(endsAt)) {
        color = 'warning'
        content = <this.WarningTip idx={idx} type="partialConstestAtLast"><Content end={endsAt} /></this.WarningTip>
      }

      if (!content) {
        content = <Content />
      }

      if (cstart.format('x') < now) {
        content = <i>{content}</i>
      }

      if (cend.format('x') < now) {
        content = <s>{content}</s>
      }
    }
    return <div className='p-1 px-2' color={color}>{content || '...'}</div>
  }

  WarningTip = ({ idx, children, type }) => {
    const id = `${type}-${idx}`

    return <>
      <DefaultTooltip content={this.warningTipMessages[type]}>
        <span id={id}>
          {children}
        </span>
      </DefaultTooltip>
    </>
  }

  Resume = ({ children }) => {
    let { selectedWinnerFrequency: { noun, cronMask } = {} } = this.state || {}
    const { winnerFrequency: frequency, winnerFrequencyOpts = {} } = this.props.data
    const { active, startsAt, endsAt } = this.props.postOpts

    const [_, setListenChanges] = useState(false)
    const [changed, setChanged] = useState(false)

    useEffect(() => {
      setListenChanges(listenChanges => {
        listenChanges && setChanged(true)
        return true
      })
    }, [active, startsAt, endsAt, frequency])

    noun = noun || winnerFrequencyOpts.noun
    cronMask = cronMask || winnerFrequencyOpts.cronMask

    const now = Date.now()
    const isStatic = frequency === 'static'
    let nextCycles = []
    let ContestsPreview = () => <div><b>No contests</b></div>

    if (active && startsAt && frequency && (!isStatic || endsAt)) {
      const interval = getCycles(isStatic ? null : cronMask, startsAt, endsAt)
      const qtd = interval.length
      const pastCylces = supressMiddle(interval.filter(([_, cend]) => cend && cend.format('x') < now), true, 2)
      nextCycles = supressMiddle(interval.filter(([_, cend]) => cend && cend.format('x') >= now), !!endsAt, 2)
      const firstPreviewContest = interval[0]
      const lastPreviewContest = interval[interval.length - 1] || []

      if (qtd) {
        const [realStartsAt] = firstPreviewContest
        const [_, realEndsAt] = lastPreviewContest
        const cycles = isStatic ? `just once contest starting at` : `${endsAt ? qtd : 'unlimited'} contests from`
        const repeating = isStatic || !realEndsAt || !noun ? '' : ` repeating every ${noun}`

        ContestsPreview = ({ color }) => <>
          <p>This configuration should generate {cycles} <Badge color={color}>{realStartsAt.format(defaultDateTimeFormat)}</Badge> {realEndsAt ? <span>to <Badge color={color}>{realEndsAt.format(defaultDateTimeFormat)}</Badge></span> : ''} {repeating}</p>
          <div className='p-1 border-2 twy-border-base-accent mt-2 rounded-xs'>
            {pastCylces.concat(nextCycles).map((dates, idx) => <this.ContestPreviewItem dates={dates} now={now} key={idx} color={color} />)}
          </div>
        </>
      }
    }

    return <Row>
      <Col size='12'>
        {active && changed ? <AlertForm name='_' color='danger'
          validations={{
            hasFlightStartDate: ({ flight_start_at }) => {
              return !!flight_start_at
            },
            hasEndDateWhenIsStatic: ({ flight_end_at, winnerFrequency }) => {
              return winnerFrequency !== 'static' || !!flight_end_at
            },
            hasNextCycles: () => {
              return !!nextCycles.length
            },
            isFlightEndGreatherThanStart: ({ flight_start_at, flight_end_at }) => {
              return !flight_end_at || flight_end_at.isAfter(flight_start_at)
            },
            isFlightDatesNotCoveringLastContests: ({ flight_start_at }) => {
              return !flight_start_at || !this.props.sweepCycles || !this.props.sweepCycles.length || this.props.sweepCycles.every(cycle => {
                return cycle.endsAt < flight_start_at.format('x')
              })
            },
            isFlightDatesCoveringAtLeastOneRepeatableContest: ({ flight_end_at }) => {
              return !nextCycles.length || !flight_end_at || !nextCycles[0] || !nextCycles[0][1] || nextCycles[0][1].isSameOrBefore(flight_end_at)
            }
          }}
          validationErrors={this.validationErrors}
        /> : null}
        {children}
        <SimpleAlert variant='info'>
          <ContestsPreview color='accent' />
        </SimpleAlert>
      </Col>
    </Row>
  }

  Form = () => {
    const { winnerFrequency, regFrequency, regMaxCap, regMode, regMinCap, winnerMaxCap, maxIncentTicket, enableIncentTicket, enableSweepWinner, drawType } = this.props.data

    return <Row>
      <Col size='4'>
        <FormGroup>
          <Label>Winner Frequency</Label>
          <Select name='winnerFrequency' required
            disabled={this.props.disabled}
            clearable={false}
            options={frequencyOptions}
            value={winnerFrequency}
            handleOnChange={this.handleChangeWinnerFrequency}
          />
        </FormGroup>
      </Col>
      <Col size='8'>
        <FormGroup>
          <Label>Number of Winners</Label>
          <Input name='winnerMaxCap' required type='number' min='1'
            disabled={this.props.disabled}
            value={winnerMaxCap}
            setValue={winnerMaxCap => this.props.onChange({ winnerMaxCap })}
          />
        </FormGroup>
      </Col>
      <Col size='4'>
        <FormGroup>
          <Label>Registration Frequency</Label>
          <Select name='regFrequency' required
            disabled={this.props.disabled}
            clearable={false}
            options={frequencyOptions}
            value={regFrequency}
            setValue={regFrequency => this.props.onChange({ regFrequency })}
          />
        </FormGroup>
      </Col>
      <Col size='8'>
        <FormGroup>
          <Label>Number of registrations for frequency</Label>
          <Input name='regMaxCap' required type='number' min='1'
            disabled={this.props.disabled}
            value={regMaxCap}
            setValue={regMaxCap => this.props.onChange({ regMaxCap })}
          />
        </FormGroup>
      </Col>
      <Col size='4'>
        <FormGroup>
          <Label>Registration Mode</Label>
          <Select name='regMode' required
            disabled={this.props.disabled}
            clearable={false}
            options={registrationModeOptions(this.props.hasConfirmation)}
            value={regMode}
            setValue={regMode => this.props.onChange({ regMode })}
          />
        </FormGroup>
      </Col>
      <Col size='8'>
        <FormGroup>
          <Label>Minimum number of registrations</Label>
          <Input name='regMinCap' required type='number' min='1'
            disabled={this.props.disabled}
            value={regMinCap}
            setValue={regMinCap => this.props.onChange({ regMinCap })}
          />
        </FormGroup>
      </Col>
      <Col size='4'>
        <FormGroup className='flex flex-col'>
          <Label>Enable incent ticket</Label>
          <Switch
            name="enableIncentTicket"
            value={enableIncentTicket}
            disabled={this.props.disabled}
            setValue={enableIncentTicket => this.props.onChange({ enableIncentTicket, maxIncentTicket: null })}
          />
        </FormGroup>
      </Col>
      <Col size='8'>
        <FormGroup>
          <Label>Max incent ticket</Label>
          <Input
            name='maxIncentTicket'
            type='number'
            min='1'
            disabled={this.props.disabled || !enableIncentTicket}
            required={enableIncentTicket}
            value={!enableIncentTicket ? '' : maxIncentTicket}
            setValue={maxIncentTicket => this.props.onChange({ maxIncentTicket })}
          />
        </FormGroup>
      </Col>
      <Col size='4'>
        <FormGroup>
          <Label>Enable winner selection</Label>
          <Switch
            name="enableSweepWinner"
            value={enableSweepWinner}
            disabled={this.props.disabled}
            setValue={enableSweepWinner => this.props.onChange({ enableSweepWinner })}
          />
        </FormGroup>
      </Col>
      <Col size='8'>
        <FormGroup>
          <Label>Draw type</Label>
          <Select
            name='drawType'
            disabled={this.props.disabled || !enableSweepWinner}
            required={enableSweepWinner}
            clearable={false}
            options={drawTypeOptions}
            value={!enableSweepWinner ? '' : drawType}
            setValue={drawType => this.props.onChange({ drawType })}
          />
        </FormGroup>
      </Col>
    </Row>
  }

  ConfirmationAlert = () => {
    const { regMode } = this.props.data

    if (regMode !== 'confirmation') {
      return null
    }

    return <SimpleAlert>
      The consumer will be redirected to <b>/s-register</b> upon successful registration. Please configure the behavior of this page or create it under your site
      <Link to='/cms/page'>pages</Link>.
    </SimpleAlert>
  }

  SCAWinnersAlert = () => {
    const { winnerMaxCap } = this.props.data
    const { getSweepIntegrations } = usePostStore()

    const loadIntegration = async () => {
      if (this.props.postOpts.id) {
        const { rows } = await getSweepIntegrations({ term: `configuration={"sweep_id":${this.props.postOpts.id}}` })
        this.scaIntegration = rows[0] || null
      }
    }

    useEffect(() => {
      loadIntegration()
    }, [])

    if (!this.scaIntegration || winnerMaxCap < 1) {
      return null
    }

    return <SimpleAlert variant='warning'>
      This Sweeps has a SCA integration assigned. SCA Promotions only supports a single Winner. Therefore, the field "Number of Winners" will be ignored on this sweeps.
    </SimpleAlert>
  }

  LinkShowContests = () => {
    if (!this.props.postOpts.id) {
      return null
    }

    return <Link to={{
      state: { sweepId: this.props.postOpts.id },
      pathname: `/cms/sweep/contests`,
    }} className='twy-text-accent flex gap-1 items-center'>
      Show All Contests for this Sweep  <SquareArrowOutUpRight size={12} />
    </Link>
  }

  render() {
    return <>
      <this.Form />
      <this.Resume>
        <this.ConfirmationAlert />
        <this.SCAWinnersAlert />
      </this.Resume>
      <this.LinkShowContests />
    </>
  }
}

function getCycles(cronMask, startDate, endDate) {
  const gen = function* () {
    if (!cronMask) {
      return yield [startDate, endDate, 1]
    }

    const interval = cron.parseExpression(startDate.format(cronMask), {
      tz: appStore.timezone,
      currentDate: startDate, // .isBefore(Moment.tz(appStore.timezone)) ? undefined : startDate
    })

    let nextCyclesLimit = endDate ? Infinity : 15
    const now = Moment.tz(appStore.timezone)

    for (let i = 0; i < Infinity; i++) {
      const ndate = interval.next()._date
      const edate = Moment(ndate).tz(appStore.timezone).subtract(1, 'seconds')

      if (edate.isAfter(now)) {
        nextCyclesLimit--
      }

      if (nextCyclesLimit <= 0) {
        break
      }

      if (endDate && edate.isSameOrAfter(endDate) && edate.isSameOrAfter(startDate)) {
        if (startDate.isBefore(endDate)) {
          nextCyclesLimit = 1
        } else break
      }

      yield [startDate, edate, i + 1]

      startDate = ndate
    }
  }
  return [...gen()]
}

export const frequencyOptions = [
  { value: 'd', label: 'Daily', noun: 'day', cronMask: '0 0 0 * * *' },
  { value: 'w', label: 'Weekly', noun: 'week', cronMask: '0 0 0 * * 0' },
  { value: 'M', label: 'Monthly', noun: 'month', cronMask: '0 0 0 1 * *' },
  { value: 'y', label: 'Yearly', noun: 'year', cronMask: '0 0 0 1 1 *' },
  { value: 'Q', label: 'Quarterly', noun: 'quarter (3 months)', cronMask: '0 0 0 1 */3 *' },
  { value: 'S', label: 'Semi-yearly', noun: 'semester (6 months)', cronMask: '0 0 0 1 */6 *' },
  { value: 'static', label: 'Just Once' },
]

export const drawTypeOptions = [
  { value: 'lottery', label: 'Lottery' },
  { value: 'sweepstake', label: 'Sweepstake' },
]

const registrationModeOptions = (hasConfirmation = true) => {
  const options = [
    { value: 'all', label: "All registrations" },
  ]

  if (hasConfirmation) {
    options.push({ value: 'confirmation', label: "E-mail Confirmation" })
  }

  return options
}

export const conversionModeOptions = registrationModeOptions()

const supressMiddle = (range, limited, pieceSize) => limited ? (range.length > pieceSize * 2 ? [...range.slice(0, pieceSize), [undefined], ...range.slice(-pieceSize)] : range) : [...range.slice(0, pieceSize * 2), [undefined]]

SweepOptions.propTypes = propTypes
SweepOptions.defaultProps = {}
